import { defineStore } from 'pinia';
import { usePage } from '@inertiajs/vue3';
import { computed } from 'vue';
import { KrakenSDK } from '@agentsoftware/kraken-ts-sdk';
import { useStatus } from '@agentsoftware/spectre-ui';
// eslint-disable-next-line import/no-cycle
import { useReportStore } from '@/Stores/Report';
import StatsData = App.Data.Report.Market.StatsData;
import EstimatedPriceChangeStatsData = App.Data.Report.Market.EstimatedPriceChangeStatsData;

// eslint-disable-next-line import/prefer-default-export
export const useMarketStatsStore = defineStore('market-stats', () => {
  const page = usePage();
  const reportStore = useReportStore();

  const salesEstimatedPriceChangeQueryStatus = useStatus();
  const salesVolumeOfSoldPropertiesQueryStatus = useStatus();
  const salesAverageTimeOnMarketQueryStatus = useStatus();
  const salesAveragePricePerSqftQueryStatus = useStatus();
  const lettingsEstimatedPriceChangeQueryStatus = useStatus();
  const lettingsAverageTimeOnMarketQueryStatus = useStatus();
  const lettingsVolumeOfLetPropertiesQueryStatus = useStatus();
  const lettingsAveragePropertyYieldQueryStatus = useStatus();

  const kraken = computed(() => new KrakenSDK({ serverURL: page.props.config.kraken.api_url }));

  const sortStats = (stats: StatsData[]) => stats.sort((a: StatsData, b: StatsData) => {
    const typesOrder = ['Detached', 'Semi-Detached', 'Terraced', 'Flats/Maisonettes'];
    return typesOrder.indexOf(a.propertyType) - typesOrder.indexOf(b.propertyType);
  });

  const sortPriceChangeStats = (stats: EstimatedPriceChangeStatsData[]) => stats.sort((a: EstimatedPriceChangeStatsData, b: EstimatedPriceChangeStatsData) => {
    const typesOrder = ['Detached', 'Semi-Detached', 'Terraced', 'Flats/Maisonettes'];
    return typesOrder.indexOf(a.propertyType) - typesOrder.indexOf(b.propertyType);
  });

  const mapStats = (stats: StatsData[]) => sortStats(stats).map((propertyType: StatsData) => ({
    label: propertyType.propertyType,
    value: propertyType.value,
  }));

  const fetchSalesEstimatedPriceChange = async () => {
    salesEstimatedPriceChangeQueryStatus.setStatus('loading');

    if (!reportStore.market.localAuthorityCode) {
      salesEstimatedPriceChangeQueryStatus.setStatus('error');
      return;
    }

    try {
      const response = await kraken.value.statsSales.getLocalAuthorityEstimatedValuesStatsSalesLocalAuthorityEstimatedValuesLocalAuthorityCodeGet(
        { xApiKey: page.props.config.kraken.api_key },
        reportStore.market.localAuthorityCode,
        undefined,
        '@mon-12mon',
        { fetchOptions: { headers: { 'Accept-Version': page.props.config.kraken.version } } },
      );

      if (response?.data.attributes.stats) {
        const mappedStats = response.data.attributes.stats.propertyType.map((propertyType) => ({
          propertyType: propertyType.propertyType as string,
          latestEstimate: propertyType.latestEstimate.value,
          values: propertyType.values.map((type) => ({ date: type.estimateDate.toString(), value: type.averageEstimatedMarketValueRounded })),
        }));

        reportStore.updateSalesMarketStats('salesEstimatedPriceChange', sortPriceChangeStats(mappedStats));
      }

      salesEstimatedPriceChangeQueryStatus.setStatus('success');
    } catch (e) {
      salesEstimatedPriceChangeQueryStatus.setStatus('error');
    }
  };

  const fetchSalesVolumeOfSoldProperties = async () => {
    salesVolumeOfSoldPropertiesQueryStatus.setStatus('loading');

    if (!reportStore.market.localAuthorityCode) {
      salesVolumeOfSoldPropertiesQueryStatus.setStatus('error');
      return;
    }

    try {
      const response = await kraken.value.statsSales.getLocalAuthorityPropertiesSoldStatsSalesLocalAuthorityPropertiesSoldLocalAuthorityCodeGet(
        { xApiKey: page.props.config.kraken.api_key },
        reportStore.market.localAuthorityCode,
        undefined,
        '@d-12mon',
        { fetchOptions: { headers: { 'Accept-Version': page.props.config.kraken.version } } },
      );

      if (response?.data.attributes.stats) {
        const mappedStats = response.data.attributes.stats.propertyType.map((propertyType) => ({
          propertyType: propertyType.propertyType as string,
          value: propertyType.totalProperties,
        }));

        reportStore.updateSalesMarketStats('salesVolumeOfSoldProperties', mappedStats);
      }

      salesVolumeOfSoldPropertiesQueryStatus.setStatus('success');
    } catch (e) {
      salesVolumeOfSoldPropertiesQueryStatus.setStatus('error');
    }
  };

  const fetchSalesAverageTimeOnMarket = async () => {
    salesAverageTimeOnMarketQueryStatus.setStatus('loading');

    if (!reportStore.market.localAuthorityCode) {
      salesAverageTimeOnMarketQueryStatus.setStatus('error');
      return;
    }

    try {
      const response = await kraken.value.statsSales.getLocalAuthorityAverageTimeOnMarketStatsSalesLocalAuthorityAverageTimeOnMarketLocalAuthorityCodeGet(
        { xApiKey: page.props.config.kraken.api_key },
        reportStore.market.localAuthorityCode,
        undefined,
        '@d-12mon',
        { fetchOptions: { headers: { 'Accept-Version': page.props.config.kraken.version } } },
      );

      if (response?.data.attributes.stats) {
        const mappedStats = response.data.attributes.stats.propertyType.map((propertyType) => ({
          propertyType: propertyType.propertyType as string,
          value: propertyType.averageDaysOnMarket,
        }));

        reportStore.updateSalesMarketStats('salesAverageTimeOnMarket', mappedStats);
      }

      salesAverageTimeOnMarketQueryStatus.setStatus('success');
    } catch (e) {
      salesAverageTimeOnMarketQueryStatus.setStatus('error');
    }
  };

  const fetchSalesAveragePricePerSqft = async () => {
    salesAveragePricePerSqftQueryStatus.setStatus('loading');

    if (!reportStore.market.localAuthorityCode) {
      salesAveragePricePerSqftQueryStatus.setStatus('error');
      return;
    }

    try {
      const response = await kraken.value.statsSales.getLocalAuthorityPricePerSquareFootStatsSalesLocalAuthorityAveragePricePerSquareFootLocalAuthorityCodeGet(
        { xApiKey: page.props.config.kraken.api_key },
        reportStore.market.localAuthorityCode,
        undefined,
        '@d-12mon',
        { fetchOptions: { headers: { 'Accept-Version': page.props.config.kraken.version } } },
      );

      if (response?.data.attributes.stats) {
        const mappedStats = response.data.attributes.stats.propertyType.map((propertyType) => ({
          propertyType: propertyType.propertyType as string,
          value: propertyType.averagePricePerSquareFoot,
        }));

        reportStore.updateSalesMarketStats('salesAveragePricePerSqft', mappedStats);
      }

      salesAveragePricePerSqftQueryStatus.setStatus('success');
    } catch (e) {
      salesAveragePricePerSqftQueryStatus.setStatus('error');
    }
  };

  const fetchLettingsEstimatedPriceChange = async () => {
    lettingsEstimatedPriceChangeQueryStatus.setStatus('loading');

    if (!reportStore.market.localAuthorityCode) {
      lettingsEstimatedPriceChangeQueryStatus.setStatus('error');
      return;
    }

    try {
      const response = await kraken.value.statsLettings.getLettingsLocalAuthorityAverageListingPriceStatsLettingsLocalAuthorityAverageListingPriceLocalAuthorityCodeGet(
        { xApiKey: page.props.config.kraken.api_key },
        reportStore.market.localAuthorityCode,
        undefined,
        '@mon-12mon',
        { fetchOptions: { headers: { 'Accept-Version': page.props.config.kraken.version } } },
      );

      if (response?.data.attributes.stats) {
        const mappedStats = response.data.attributes.stats.propertyType.map((propertyType) => ({
          propertyType: propertyType.propertyType as string,
          latestEstimate: null,
          values: propertyType.stats.map((type) => ({ date: type.date.toString(), value: type.averageListingPrice ?? 0 })),
        }));

        reportStore.updateLettingsMarketStats('lettingsEstimatedPriceChange', sortPriceChangeStats(mappedStats));
      }

      lettingsEstimatedPriceChangeQueryStatus.setStatus('success');
    } catch (e) {
      lettingsEstimatedPriceChangeQueryStatus.setStatus('error');
    }
  };

  const fetchLettingsVolumeOfLetProperties = async () => {
    lettingsVolumeOfLetPropertiesQueryStatus.setStatus('loading');

    if (!reportStore.market.localAuthorityCode) {
      lettingsVolumeOfLetPropertiesQueryStatus.setStatus('error');
      return;
    }

    try {
      const response = await kraken.value.statsLettings.getLettingsLocalAuthorityTotalPropertiesLetStatsLettingsLocalAuthorityTotalPropertiesLetLocalAuthorityCodeGet(
        { xApiKey: page.props.config.kraken.api_key },
        reportStore.market.localAuthorityCode,
        undefined,
        '@d-12mon',
        { fetchOptions: { headers: { 'Accept-Version': page.props.config.kraken.version } } },
      );

      if (response?.data.attributes.stats) {
        const mappedStats = response.data.attributes.stats.propertyType.map((propertyType) => ({
          propertyType: propertyType.propertyType as string,
          value: propertyType.totalProperties,
        }));

        reportStore.updateLettingsMarketStats('lettingsVolumeOfLetProperties', mappedStats);
      }

      lettingsVolumeOfLetPropertiesQueryStatus.setStatus('success');
    } catch (e) {
      lettingsVolumeOfLetPropertiesQueryStatus.setStatus('error');
    }
  };

  const fetchLettingsAverageTimeOnMarket = async () => {
    lettingsAverageTimeOnMarketQueryStatus.setStatus('loading');

    if (!reportStore.market.localAuthorityCode) {
      lettingsAverageTimeOnMarketQueryStatus.setStatus('error');
      return;
    }

    try {
      const response = await kraken.value.statsLettings.getLettingsLocalAuthorityAverageTimeOnMarketStatsLettingsLocalAuthorityAverageTimeOnMarketLocalAuthorityCodeGet(
        { xApiKey: page.props.config.kraken.api_key },
        reportStore.market.localAuthorityCode,
        undefined,
        '@d-12mon',
        { fetchOptions: { headers: { 'Accept-Version': page.props.config.kraken.version } } },
      );

      if (response?.data.attributes.stats) {
        const mappedStats = response.data.attributes.stats.propertyType.map((propertyType) => ({
          propertyType: propertyType.propertyType as string,
          value: propertyType.averageDaysOnMarket,
        }));

        reportStore.updateLettingsMarketStats('lettingsAverageTimeOnMarket', mappedStats);
      }

      lettingsAverageTimeOnMarketQueryStatus.setStatus('success');
    } catch (e) {
      lettingsAverageTimeOnMarketQueryStatus.setStatus('error');
    }
  };

  const fetchLettingsAveragePropertyYield = async () => {
    lettingsAveragePropertyYieldQueryStatus.setStatus('loading');

    if (!reportStore.market.localAuthorityCode) {
      lettingsAveragePropertyYieldQueryStatus.setStatus('error');
      return;
    }

    try {
      const response = await kraken.value.statsLettings.getLettingsLocalAuthorityAverageTimeOnMarketStatsLettingsLocalAuthorityAverageAnnualYieldLocalAuthorityCodeGet(
        { xApiKey: page.props.config.kraken.api_key },
        reportStore.market.localAuthorityCode,
        { fetchOptions: { headers: { 'Accept-Version': page.props.config.kraken.version } } },
      );

      if (response?.data.attributes.stats) {
        const mappedStats = response.data.attributes.stats.propertyType.map((propertyType) => ({
          propertyType: propertyType.propertyType as string,
          value: propertyType.averageAnnualYield,
        }));

        reportStore.updateLettingsMarketStats('lettingsAveragePropertyYield', mappedStats);
      }

      lettingsAveragePropertyYieldQueryStatus.setStatus('success');
    } catch (e) {
      lettingsAveragePropertyYieldQueryStatus.setStatus('error');
    }
  };

  const fetchStats = async () => {
    fetchSalesEstimatedPriceChange();
    fetchSalesVolumeOfSoldProperties();
    fetchSalesAverageTimeOnMarket();
    fetchSalesAveragePricePerSqft();
    fetchLettingsEstimatedPriceChange();
    fetchLettingsVolumeOfLetProperties();
    fetchLettingsAverageTimeOnMarket();
    fetchLettingsAveragePropertyYield();
    reportStore.setMarketStatQueryStatusesToSuccess();
  };

  return {
    salesEstimatedPriceChangeQueryStatus,
    salesVolumeOfSoldPropertiesQueryStatus,
    salesAverageTimeOnMarketQueryStatus,
    salesAveragePricePerSqftQueryStatus,
    lettingsAverageTimeOnMarketQueryStatus,
    lettingsEstimatedPriceChangeQueryStatus,
    lettingsVolumeOfLetPropertiesQueryStatus,
    lettingsAveragePropertyYieldQueryStatus,
    fetchStats,
    mapStats,
    sortStats,
    sortPriceChangeStats,
  };
});
